// Daten der Objektliste: drei Tabellen gebündelt geladen (keine Abfrage je Objekt),
// dazu Ticket-Quote und die Auswahl für „Zuständig“.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase/client';
import { alleSeiten } from '../supabase/alleSeiten';
import { sevEinheitenAus, type EinheitListeRoh, type MieterListeRoh, type ObjektListeRoh } from './portfolio';
import { heuteIso } from './format';

export interface ZustaendigOption { id: string; name: string; typ: 'mitarbeiter' | 'team' }

export interface TicketQuote {
  /** Tickets der letzten 12 Monate (ohne gelöschte) */
  gesamt: number;
  erledigt: number;
}

export interface ObjektListeDaten {
  objekte: ObjektListeRoh[];
  einheiten: EinheitListeRoh[];
  mieter: MieterListeRoh[];
  /** #596: Einheiten mit laufendem SEV-Vertrag (zählen bei WEG für den Leerstand) */
  sevEinheiten: Set<string>;
  tickets: TicketQuote | null;
  mitarbeiter: ZustaendigOption[];
  teams: ZustaendigOption[];
}

async function ladeTicketQuote(): Promise<TicketQuote | null> {
  const seit = new Date();
  seit.setFullYear(seit.getFullYear() - 1);
  const basis = () => supabase.from('tickets').select('id', { count: 'exact', head: true })
    .is('geloescht_am', null).gte('created_at', seit.toISOString());
  const [alle, fertig] = await Promise.all([basis(), basis().in('status', ['erledigt', 'geschlossen'])]);
  if (alle.error || fertig.error) return null;
  return { gesamt: alle.count ?? 0, erledigt: fertig.count ?? 0 };
}

export type BestandRoh = Pick<ObjektListeDaten, 'objekte' | 'einheiten' | 'mieter' | 'sevEinheiten'>;

/** Objekte, Einheiten, Mieter und SEV-Einheiten – gemeinsame Grundlage für Objektliste und Dashboard (#596). */
export async function ladeBestand(): Promise<BestandRoh> {
  const [objekte, einheiten, mieter, sev] = await Promise.all([
    alleSeiten<ObjektListeRoh>((von, bis) => supabase.from('objekte').select('*').eq('ist_verwaltung', false).order('objektnummer').range(von, bis)),
    alleSeiten<EinheitListeRoh>((von, bis) => supabase.from('objekt_einheiten').select('id, objekt_id, wohnflaeche, uebergeordnete_einheit_id').order('id').range(von, bis)),
    alleSeiten<MieterListeRoh>((von, bis) => supabase.from('mieter')
      .select('objekt_id, einheit_id, status, kaltmiete, miet_historie, mietbeginn, mietende').order('id').range(von, bis)),
    alleSeiten<{ einheit_id: string; sev_status: string | null; vertrag_bis: string | null }>((von, bis) => supabase.from('einheit_eigentuemer')
      .select('einheit_id, sev_status, vertrag_bis').not('sev_status', 'is', null).order('einheit_id').range(von, bis)),
  ]);
  return { objekte, einheiten, mieter, sevEinheiten: sevEinheitenAus(sev, heuteIso()) };
}

async function lade(): Promise<ObjektListeDaten> {
  const [{ objekte, einheiten, mieter, sevEinheiten }, tickets, profile, teams] = await Promise.all([
    ladeBestand(),
    ladeTicketQuote(),
    supabase.from('profiles').select('id, full_name').or('role.eq.team,role.eq.admin').eq('zuweisbar', true).order('full_name'),
    supabase.from('teams').select('id, name').order('name'),
  ]);
  return {
    objekte,
    einheiten,
    mieter,
    sevEinheiten,
    tickets,
    mitarbeiter: ((profile.data ?? []) as { id: string; full_name: string | null }[])
      .filter(p => p.full_name).map(p => ({ id: p.id, name: p.full_name ?? '', typ: 'mitarbeiter' as const })),
    teams: ((teams.data ?? []) as { id: string; name: string }[]).map(t => ({ id: t.id, name: t.name, typ: 'team' as const })),
  };
}

export type ListenZustand =
  | { status: 'laedt' }
  | { status: 'fehler'; meldung: string }
  | { status: 'ok'; daten: ObjektListeDaten };

export function useObjektListe(): { zustand: ListenZustand; neuLaden: () => void; setzeObjekt: (id: string, aenderung: Partial<ObjektListeRoh>) => void } {
  const [zustand, setZustand] = useState<ListenZustand>({ status: 'laedt' });
  const [runde, setRunde] = useState(0);

  useEffect(() => {
    let aktiv = true;
    lade()
      .then(d => { if (aktiv) setZustand({ status: 'ok', daten: d }); })
      .catch((e: unknown) => { if (aktiv) setZustand(z => (z.status === 'ok' ? z : { status: 'fehler', meldung: e instanceof Error ? e.message : 'Unbekannter Fehler' })); });
    return () => { aktiv = false; };
  }, [runde]);

  const neuLaden = useCallback(() => setRunde(r => r + 1), []);

  /** Optimistische Änderung eines Objekts (z. B. Zuständig), ohne alles neu zu laden. */
  const setzeObjekt = useCallback((id: string, aenderung: Partial<ObjektListeRoh>) => {
    setZustand(z => (z.status !== 'ok' ? z : {
      status: 'ok',
      daten: { ...z.daten, objekte: z.daten.objekte.map(o => (o.id === id ? { ...o, ...aenderung } : o)) },
    }));
  }, []);

  return { zustand, neuLaden, setzeObjekt };
}

/** Zuständigkeit setzen – gleiche Schreiblogik wie bisher (objekte + entity_links). */
export async function setzeZustaendig(objektId: string, ziel: ZustaendigOption | null): Promise<void> {
  const { error } = await supabase.from('objekte').update({
    zustaendig_id: ziel ? ziel.id : null,
    zustaendig: ziel ? ziel.name : 'Global',
    zustaendig_typ: ziel ? ziel.typ : null,
  }).eq('id', objektId);
  if (error) throw new Error(error.message);

  // Maßgeblich ist objekte.zustaendig_*; der Link ist die Spiegelung für Suchen/Ansichten.
  // Wie bisher bricht ein Link-Fehler den Vorgang nicht ab – er wird aber gemeldet.
  // Hinweis: Laut Repo-Schema kennt der CHECK valid_target den Typ 'team' nicht.
  const weg = await supabase.from('entity_links').delete()
    .eq('source_type', 'objekt').eq('source_id', objektId).eq('link_type', 'zustaendigkeit');
  if (weg.error) console.warn('Zuständigkeits-Link entfernen fehlgeschlagen:', weg.error.message);
  if (ziel) {
    const neu = await supabase.from('entity_links').insert({
      source_type: 'objekt', source_id: objektId,
      target_type: ziel.typ === 'team' ? 'team' : 'profile', target_id: ziel.id,
      link_type: 'zustaendigkeit',
    });
    if (neu.error) console.warn('Zuständigkeits-Link anlegen fehlgeschlagen:', neu.error.message);
  }
}

export async function loescheObjekt(objektId: string): Promise<void> {
  const { error } = await supabase.from('objekte').delete().eq('id', objektId);
  if (error) throw new Error(error.message);
}
